<script setup>
// Campo padrão de data única (e data com hora) da Talkvia, no lugar do
// calendário nativo do navegador. O valor segue o formato dos campos nativos
// (`aaaa-mm-dd` ou `aaaa-mm-ddThh:mm`), então substitui `type="date"` e
// `type="datetime-local"` sem mudar a gravação. Intervalos usam o DatePicker.
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  useId,
  watch,
} from 'vue';
import {
  displayValue,
  formatValue,
  monthGrid,
  parseValue,
  toDateKey,
} from './dateFieldValue';

const props = defineProps({
  label: { type: String, default: '' },
  withTime: { type: Boolean, default: false },
  min: { type: String, default: '' },
  max: { type: String, default: '' },
  placeholder: { type: String, default: '' },
  disabled: { type: Boolean, default: false },
  clearable: { type: Boolean, default: true },
  message: { type: String, default: '' },
  messageType: { type: String, default: 'info' },
  id: { type: String, default: '' },
  size: { type: String, default: 'md' },
});

const modelValue = defineModel({ type: String, default: '' });

const locale = (document.documentElement.lang || 'pt-BR').replace('_', '-');
const english = locale.toLowerCase().startsWith('en');
const copy = english
  ? {
      choose: 'Select a date',
      chooseTime: 'Select date and time',
      today: 'Today',
      now: 'Now',
      clear: 'Clear',
      apply: 'Apply',
      previous: 'Previous month',
      next: 'Next month',
      hour: 'Hour',
      minute: 'Minute',
    }
  : {
      choose: 'Selecionar data',
      chooseTime: 'Selecionar data e hora',
      today: 'Hoje',
      now: 'Agora',
      clear: 'Limpar',
      apply: 'Aplicar',
      previous: 'Mês anterior',
      next: 'Próximo mês',
      hour: 'Hora',
      minute: 'Minuto',
    };

const uniqueId = props.id || `date-field-${useId()}`;
const root = ref(null);
const panel = ref(null);
const host = ref(null);
const isOpen = ref(false);
const position = ref({});
const viewMonth = ref(new Date());
const draftDay = ref(null);
const draftHour = ref(9);
const draftMinute = ref(0);

const weekdays = computed(() => {
  const formatter = new Intl.DateTimeFormat(locale, { weekday: 'narrow' });
  return Array.from({ length: 7 }, (_, index) =>
    formatter.format(new Date(2024, 8, 1 + index))
  );
});
const monthLabel = computed(() =>
  new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' }).format(
    viewMonth.value
  )
);
const days = computed(() => monthGrid(viewMonth.value));
const todayKey = computed(() => toDateKey(new Date()));
const shown = computed(() =>
  displayValue(modelValue.value, props.withTime, locale)
);
const placeholderText = computed(
  () => props.placeholder || (props.withTime ? copy.chooseTime : copy.choose)
);
const minKey = computed(() => props.min.slice(0, 10));
const maxKey = computed(() => props.max.slice(0, 10));
const hours = Array.from({ length: 24 }, (_, hour) => hour);
const minutes = computed(() => {
  const list = Array.from({ length: 12 }, (_, index) => index * 5);
  return list.includes(draftMinute.value)
    ? list
    : [...list, draftMinute.value].sort((a, b) => a - b);
});

const isDisabledDay = day => {
  const key = toDateKey(day);
  return Boolean(
    (minKey.value && key < minKey.value) || (maxKey.value && key > maxKey.value)
  );
};

const draftValue = computed(() => {
  if (!draftDay.value) return '';
  const date = new Date(draftDay.value);
  date.setHours(draftHour.value, draftMinute.value, 0, 0);
  return formatValue(date, props.withTime);
});
const draftOutOfRange = computed(() => {
  const value = draftValue.value;
  if (!value) return true;
  const limit = bound => bound.slice(0, value.length);
  return Boolean(
    (props.min && value < limit(props.min)) ||
      (props.max && value > limit(props.max))
  );
});

// Com hora, cada escolha (dia, hora, minuto, "Agora") já preenche o campo e o
// calendário continua aberto para ajustar o horário; "Aplicar" só fecha.
const fillDraft = () => {
  if (props.withTime && !draftOutOfRange.value)
    modelValue.value = draftValue.value;
};
const timeColumns = computed(() => [
  {
    key: 'hour',
    label: copy.hour,
    values: hours,
    selected: draftHour.value,
    select: value => {
      draftHour.value = value;
      fillDraft();
    },
  },
  {
    key: 'minute',
    label: copy.minute,
    values: minutes.value,
    selected: draftMinute.value,
    select: value => {
      draftMinute.value = value;
      fillDraft();
    },
  },
]);

const updatePosition = async () => {
  if (!isOpen.value) return;
  await nextTick();
  if (!root.value || !panel.value) return;
  const trigger = root.value.getBoundingClientRect();
  const padding = 8;
  const viewport = window.visualViewport;
  const width = viewport?.width || window.innerWidth;
  const height = viewport?.height || window.innerHeight;
  const originX = viewport?.offsetLeft || 0;
  const originY = viewport?.offsetTop || 0;
  const panelWidth = Math.min(props.withTime ? 400 : 296, width - padding * 2);
  const panelHeight = Math.min(panel.value.scrollHeight, height - padding * 2);
  const left = Math.max(
    originX + padding,
    Math.min(trigger.left, originX + width - panelWidth - padding)
  );
  const below = trigger.bottom + 6;
  const top =
    below + panelHeight <= originY + height - padding
      ? below
      : Math.max(originY + padding, trigger.top - panelHeight - 6);
  position.value = {
    left: `${left}px`,
    top: `${top}px`,
    width: `${panelWidth}px`,
    maxHeight: `${height - padding * 2}px`,
  };
  // Dentro de um <dialog> com transform, `fixed` passa a ser relativo a ele.
  await nextTick();
  if (!panel.value) return;
  const actual = panel.value.getBoundingClientRect();
  position.value = {
    ...position.value,
    left: `${left - (actual.left - left)}px`,
    top: `${top - (actual.top - top)}px`,
  };
};

const syncDraft = () => {
  const current = parseValue(modelValue.value);
  const base = current || parseValue(props.min) || new Date();
  viewMonth.value = new Date(base.getFullYear(), base.getMonth(), 1);
  draftDay.value = current
    ? new Date(current.getFullYear(), current.getMonth(), current.getDate())
    : null;
  draftHour.value = current ? current.getHours() : 9;
  draftMinute.value = current ? current.getMinutes() : 0;
};

const close = (focusTrigger = false) => {
  isOpen.value = false;
  if (focusTrigger) root.value?.querySelector('button')?.focus();
};
const toggle = () => {
  if (props.disabled) return;
  if (isOpen.value) {
    close();
    return;
  }
  syncDraft();
  host.value = root.value?.closest('dialog') || document.body;
  isOpen.value = true;
  updatePosition();
};

const commit = value => {
  modelValue.value = value;
  close(true);
};
const selectDay = day => {
  if (isDisabledDay(day)) return;
  draftDay.value = new Date(day.getFullYear(), day.getMonth(), day.getDate());
  if (day.getMonth() !== viewMonth.value.getMonth())
    viewMonth.value = new Date(day.getFullYear(), day.getMonth(), 1);
  if (props.withTime) {
    fillDraft();
    return;
  }
  if (!draftOutOfRange.value) commit(draftValue.value);
};
const selectToday = () => {
  const now = new Date();
  draftDay.value = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  viewMonth.value = new Date(now.getFullYear(), now.getMonth(), 1);
  if (props.withTime) {
    draftHour.value = now.getHours();
    draftMinute.value = now.getMinutes();
    fillDraft();
    return;
  }
  if (!draftOutOfRange.value) commit(draftValue.value);
};
const apply = () => {
  if (!draftOutOfRange.value) commit(draftValue.value);
};
const clear = () => commit('');
const moveMonth = step => {
  viewMonth.value = new Date(
    viewMonth.value.getFullYear(),
    viewMonth.value.getMonth() + step,
    1
  );
  updatePosition();
};

const onOutside = event => {
  if (
    isOpen.value &&
    !root.value?.contains(event.target) &&
    !panel.value?.contains(event.target)
  )
    close();
};
const onKeydown = event => {
  if (isOpen.value && event.key === 'Escape') {
    event.preventDefault();
    event.stopPropagation();
    close(true);
  }
};
watch(isOpen, open => {
  if (!open) return;
  nextTick(() =>
    panel.value
      ?.querySelectorAll('[data-selected="true"]')
      .forEach(item => item.scrollIntoView?.({ block: 'center' }))
  );
});
onMounted(() => {
  document.addEventListener('pointerdown', onOutside);
  document.addEventListener('keydown', onKeydown, true);
  window.addEventListener('resize', updatePosition);
  window.addEventListener('scroll', updatePosition, true);
  window.visualViewport?.addEventListener('resize', updatePosition);
});
onBeforeUnmount(() => {
  document.removeEventListener('pointerdown', onOutside);
  document.removeEventListener('keydown', onKeydown, true);
  window.removeEventListener('resize', updatePosition);
  window.removeEventListener('scroll', updatePosition, true);
  window.visualViewport?.removeEventListener('resize', updatePosition);
});

const dayClass = day => {
  const key = toDateKey(day);
  const selected = draftDay.value && key === toDateKey(draftDay.value);
  if (selected) return 'bg-n-brand text-white font-semibold';
  const classes = [];
  if (day.getMonth() !== viewMonth.value.getMonth())
    classes.push('text-n-slate-9');
  else classes.push('text-n-slate-12');
  if (key === todayKey.value) classes.push('ring-1 ring-inset ring-n-brand');
  return classes.join(' ');
};
const pad = value => String(value).padStart(2, '0');
</script>

<template>
  <div ref="root" class="relative flex min-w-0 flex-col gap-1">
    <label
      v-if="label"
      :for="uniqueId"
      class="mb-0.5 text-heading-3 text-n-slate-12"
    >
      {{ label }}
    </label>
    <div class="relative">
      <button
        :id="uniqueId"
        type="button"
        class="flex w-full items-center gap-2 rounded-lg bg-n-alpha-black2 text-left outline outline-1 -outline-offset-1 transition-colors disabled:cursor-not-allowed disabled:opacity-50"
        :class="[
          size === 'sm' ? 'h-8 px-2 text-xs' : 'h-10 px-3 text-sm',
          isOpen
            ? 'outline-n-brand'
            : messageType === 'error' && message
              ? 'outline-n-ruby-8'
              : 'outline-n-weak hover:outline-n-slate-6',
          shown ? 'text-n-slate-12' : 'text-n-slate-10',
        ]"
        :aria-expanded="isOpen"
        aria-haspopup="dialog"
        :disabled="disabled"
        data-date-field-trigger
        @click="toggle"
      >
        <span
          class="i-hugeicons-calendar-03 size-4 shrink-0 text-n-slate-10"
          aria-hidden="true"
        />
        <span class="min-w-0 flex-1 truncate">
          {{ shown || placeholderText }}
        </span>
      </button>
      <button
        v-if="clearable && modelValue && !disabled"
        type="button"
        class="absolute inset-y-0 right-0 flex items-center px-3 text-n-slate-10 hover:text-n-slate-12"
        :aria-label="copy.clear"
        :title="copy.clear"
        @click="modelValue = ''"
      >
        <span class="i-hugeicons-cancel-01 size-4" aria-hidden="true" />
      </button>
    </div>
    <p
      v-if="message"
      class="m-0 text-xs"
      :class="messageType === 'error' ? 'text-n-ruby-11' : 'text-n-slate-11'"
    >
      {{ message }}
    </p>

    <Teleport v-if="isOpen" :to="host">
      <div
        ref="panel"
        class="fixed z-[10000] overflow-auto rounded-xl border border-n-weak bg-n-solid-1 p-3 shadow-lg"
        :style="position"
        data-dialog-floating-layer="true"
        role="dialog"
        :aria-label="label || placeholderText"
        @click.stop
        @touchstart.stop
      >
        <div class="flex gap-3">
          <div class="min-w-0 flex-1">
            <div class="mb-2 flex items-center justify-between gap-2">
              <button
                type="button"
                class="flex size-8 items-center justify-center rounded-lg text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12"
                :aria-label="copy.previous"
                @click="moveMonth(-1)"
              >
                <span class="i-hugeicons-arrow-left-01 size-4" />
              </button>
              <span
                class="text-sm font-semibold capitalize text-n-slate-12"
                aria-live="polite"
              >
                {{ monthLabel }}
              </span>
              <button
                type="button"
                class="flex size-8 items-center justify-center rounded-lg text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12"
                :aria-label="copy.next"
                @click="moveMonth(1)"
              >
                <span class="i-hugeicons-arrow-right-01 size-4" />
              </button>
            </div>
            <div class="grid grid-cols-7 gap-0.5 text-center">
              <span
                v-for="(weekday, index) in weekdays"
                :key="index"
                class="py-1 text-[11px] font-medium uppercase text-n-slate-10"
              >
                {{ weekday }}
              </span>
              <button
                v-for="day in days"
                :key="toDateKey(day)"
                type="button"
                class="flex aspect-square items-center justify-center rounded-lg text-sm transition-colors enabled:hover:bg-n-alpha-2 disabled:cursor-not-allowed disabled:opacity-30"
                :class="dayClass(day)"
                :disabled="isDisabledDay(day)"
                :aria-pressed="
                  !!draftDay && toDateKey(day) === toDateKey(draftDay)
                "
                :aria-label="
                  day.toLocaleDateString(locale, { dateStyle: 'full' })
                "
                @click="selectDay(day)"
              >
                {{ day.getDate() }}
              </button>
            </div>
          </div>

          <div
            v-if="withTime"
            class="flex shrink-0 gap-2 border-l border-n-weak pl-3"
          >
            <div
              v-for="column in timeColumns"
              :key="column.key"
              class="flex w-14 flex-col"
            >
              <span
                class="pb-1.5 text-center text-[11px] font-medium uppercase text-n-slate-10"
              >
                {{ column.label }}
              </span>
              <div
                class="date-field-time-list flex max-h-60 flex-col gap-1 overflow-y-auto py-1"
                role="listbox"
                :aria-label="column.label"
              >
                <button
                  v-for="value in column.values"
                  :key="value"
                  type="button"
                  role="option"
                  class="mx-0.5 flex h-8 shrink-0 items-center justify-center rounded-lg text-sm tabular-nums transition-colors"
                  :class="
                    value === column.selected
                      ? 'bg-n-brand font-semibold text-white'
                      : 'text-n-slate-12 hover:bg-n-alpha-2'
                  "
                  :aria-selected="value === column.selected"
                  :data-selected="value === column.selected"
                  v-bind="{ [`data-${column.key}`]: value }"
                  @click="column.select(value)"
                >
                  {{ pad(value) }}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div
          class="mt-3 flex items-center justify-between gap-2 border-t border-n-weak pt-3"
        >
          <div class="flex gap-1">
            <button
              type="button"
              class="rounded-lg px-2.5 py-1.5 text-xs font-medium text-n-brand hover:bg-n-alpha-2"
              @click="selectToday"
            >
              {{ withTime ? copy.now : copy.today }}
            </button>
            <button
              v-if="clearable"
              type="button"
              class="rounded-lg px-2.5 py-1.5 text-xs font-medium text-n-slate-11 hover:bg-n-alpha-2"
              @click="clear"
            >
              {{ copy.clear }}
            </button>
          </div>
          <button
            v-if="withTime"
            type="button"
            class="rounded-lg bg-n-brand px-3 py-1.5 text-xs font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
            :disabled="draftOutOfRange"
            data-date-field-apply
            @click="apply"
          >
            {{ copy.apply }}
          </button>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<style scoped>
/* Colunas de hora e minuto: rolagem sem barra visível e esmaecido nas bordas. */
.date-field-time-list {
  scrollbar-width: none;
  mask-image: linear-gradient(
    to bottom,
    transparent,
    #000 0.75rem,
    #000 calc(100% - 0.75rem),
    transparent
  );
}

.date-field-time-list::-webkit-scrollbar {
  display: none;
}
</style>
